import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface AdBannerProps {
  slot: string;
  format?: 'auto' | 'fluid' | 'rectangle';
  onAdLoad?: () => void;
  responsive?: 'true' | 'false';
  layoutKey?: string;
  style?: React.CSSProperties;
}

const AdBanner: React.FC<AdBannerProps> = ({ slot, format = 'auto', responsive = 'true', layoutKey, style, onAdLoad }) => {
  const { pathname } = useLocation();
  const adRef = useRef<HTMLModElement>(null);
  
  // ★重要：一度だけpushするための強力なストッパー
  const pushed = useRef(false);

  // ページ遷移時にストッパーをリセット
  useEffect(() => {
    pushed.current = false;
  }, [pathname]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const checkAndPush = (attempts: number) => {
      if (attempts <= 0) return;

      const adElement = adRef.current;
      
      // 要素が存在し、幅が確保されており、かつ「まだpushしていない」場合のみ実行
      if (adElement && adElement.offsetWidth > 0 && !pushed.current) {
        try {
          const windowAny = window as any;
          const adsbygoogle = (windowAny.adsbygoogle = windowAny.adsbygoogle || []);
          const status = adElement.getAttribute('data-adsbygoogle-status');
          
          if (!status) {
            adsbygoogle.push({});
            pushed.current = true; // 二度と実行させない
            
            // FullscreenAd側に読み込み完了を伝える
            if (onAdLoad) {
              onAdLoad();
            }
          }
        } catch (e) {
          console.error('AdSense error:', e);
        }
      } else if (!pushed.current) {
        // まだ幅が0なら300ms後に再確認
        timer = setTimeout(() => checkAndPush(attempts - 1), 300);
      }
    };

    // 少しだけ初期遅延を入れてから幅のチェックを開始（アニメーション対策）
    timer = setTimeout(() => checkAndPush(10), 300);

    return () => clearTimeout(timer);
  }, [pathname, onAdLoad]);

  return (
    <div 
      key={pathname} 
      style={{ 
        overflow: 'hidden', 
        textAlign: 'center', 
        width: '100%',       // ★重要：親の幅を確実に100%継承させる
        minHeight: '100px', 
        // maxHeight: '100px' は広告を潰す原因だったため削除！
        ...style
      }}
    >
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', width: '100%' }} // ★重要：insタグにも幅を明示
        data-ad-client="ca-pub-1656424757905016" 
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive}
        {...(layoutKey ? { 'data-ad-layout-key': layoutKey } : {})}
      />
    </div>
  );
};

export default AdBanner;